import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface Tenant {
  id: string;
  name: string;
  homeRegion: string;
}

export interface Region {
  id: string;
  name: string;
}

export interface ScopeAdapter {
  listTenants(): Promise<ReadonlyArray<Tenant>>;
  listRegions(): Promise<ReadonlyArray<Region>>;
}

interface ScopeContextValue {
  tenants: ReadonlyArray<Tenant>;
  regions: ReadonlyArray<Region>;
  tenant: string | null;
  region: string | null;
  setTenant: (id: string) => void;
  setRegion: (id: string) => void;
  loading: boolean;
  /** True when the active tenant is fixed by the auth session and cannot be switched. */
  tenantLocked: boolean;
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

const STORAGE_KEY = 'vex.scope';

function readUrlScope(): { region: string | null } {
  if (typeof window === 'undefined') return { region: null };
  const p = new URLSearchParams(window.location.search);
  return { region: p.get('region') };
}

function writeUrlScope(region: string | null) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (region) url.searchParams.set('region', region);
  else url.searchParams.delete('region');
  // Tenant is auth-derived and never enters the URL.
  url.searchParams.delete('tenant');
  window.history.replaceState({}, '', url.toString());
}

export interface ScopeProviderProps {
  adapter: ScopeAdapter;
  /**
   * Tenant ID derived from the auth session. When set, the tenant is
   * locked — UI must not present a picker, setTenant() becomes a no-op,
   * and cross-tenant access only happens via role assumption.
   */
  lockedTenant?: string;
  /** Used only when `lockedTenant` is absent (e.g. story decorators). */
  defaultTenant?: string;
  defaultRegion?: string;
  children: ReactNode;
}

export function ScopeProvider({
  adapter,
  lockedTenant,
  defaultTenant,
  defaultRegion,
  children,
}: ScopeProviderProps) {
  const [tenants, setTenants] = useState<ReadonlyArray<Tenant>>([]);
  const [regions, setRegions] = useState<ReadonlyArray<Region>>([]);
  const [tenant, setTenantState] = useState<string | null>(lockedTenant ?? null);
  const [region, setRegionState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all([adapter.listTenants(), adapter.listRegions()]).then(([ts, rs]) => {
      if (!alive) return;
      setTenants(ts);
      setRegions(rs);
      const urlScope = readUrlScope();
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
      const cached = stored ? (JSON.parse(stored) as { region?: string }) : {};
      // Tenant is auth-locked when present; otherwise fall back to defaultTenant.
      const t = lockedTenant ?? defaultTenant ?? ts[0]?.id ?? null;
      const r = urlScope.region ?? cached.region ?? defaultRegion ?? rs[0]?.id ?? null;
      setTenantState(t);
      setRegionState(r);
      writeUrlScope(r);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [adapter, lockedTenant, defaultTenant, defaultRegion]);

  // Pick up auth-derived tenant changes (e.g. signed in / signed out / assumed role).
  useEffect(() => {
    if (lockedTenant && lockedTenant !== tenant) setTenantState(lockedTenant);
  }, [lockedTenant, tenant]);

  const setTenant = useCallback(
    (id: string) => {
      if (lockedTenant) {
        console.warn(
          `[ScopeProvider] Refusing to switch tenant to "${id}": tenant is auth-locked to "${lockedTenant}". Use AssumeRole to act in another tenant.`,
        );
        return;
      }
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setTenantState(id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ region }));
      }
    },
    [lockedTenant, region],
  );

  const setRegion = useCallback(
    (id: string) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      setRegionState(id);
      writeUrlScope(id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ region: id }));
      }
    },
    [],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      const parsed = JSON.parse(e.newValue) as { region?: string };
      if (parsed.region && parsed.region !== region) setRegionState(parsed.region);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [region]);

  const value = useMemo<ScopeContextValue>(
    () => ({
      tenants,
      regions,
      tenant,
      region,
      setTenant,
      setRegion,
      loading,
      tenantLocked: !!lockedTenant,
    }),
    [tenants, regions, tenant, region, setTenant, setRegion, loading, lockedTenant],
  );
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error('useScope must be used inside <ScopeProvider>');
  return ctx;
}

export function createMockScopeAdapter(
  tenants: ReadonlyArray<Tenant>,
  regions: ReadonlyArray<Region>,
): ScopeAdapter {
  return {
    async listTenants() {
      return tenants;
    },
    async listRegions() {
      return regions;
    },
  };
}
